import { PriceChangesChart } from '@toggle/design-system';
import { addDays, isFriday, isSameMinute } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { getMarketHours } from '~/components/price-display-view/helpers/price-display-helpers';
import { useChartTheme } from '~/views/turbo-chart/hooks/use-chart-theme/useChartTheme';

import * as S from '../MarketReactionCharts.styles';

export interface ThemeMarketReactionChartProps {
  createdAt: string;
  tsData: { timestamp: string; value: number }[];
}

export const ThemeMarketReactionChart = ({
  createdAt,
  tsData,
}: ThemeMarketReactionChartProps) => {
  const { t } = useTranslation(['earnings']);
  const { colors } = useChartTheme(['soft']);
  const fillColors = {
    positive: colors.threshold.bullish.value,
    negative: colors.threshold.bearish.value,
  };

  const reportingDateET = toZonedTime(createdAt, 'America/New_York');
  const isReportingFriday = isFriday(reportingDateET);
  const { isPreMarket: isReportingPreMarket } = getMarketHours(
    new Date(createdAt)
  );

  const fromDate = addDays(
    new Date(
      reportingDateET.getUTCFullYear(),
      reportingDateET.getUTCMonth(),
      reportingDateET.getUTCDate(),
      9,
      30,
      0,
      0
    ),
    isReportingPreMarket ? -1 : 0
  );

  const fromDateUTC = fromZonedTime(fromDate, 'America/New_York');

  const daysToAdd = isReportingPreMarket ? 0 : isReportingFriday ? 3 : 1;
  const toDate = addDays(
    new Date(
      reportingDateET.getUTCFullYear(),
      reportingDateET.getUTCMonth(),
      reportingDateET.getUTCDate(),
      16,
      0,
      0,
      0
    ),
    daysToAdd
  );

  const toDateUTC = fromZonedTime(toDate, 'America/New_York');

  if (!tsData?.length) {
    return null;
  }

  const data =
    tsData
      ?.filter(item => {
        const itemDate = new Date(item.timestamp);
        return itemDate >= fromDateUTC && itemDate <= toDateUTC;
      })
      .map(item => ({
        date: toZonedTime(new Date(item.timestamp), 'America/New_York'),
        value: item.value,
      })) ?? [];

  const line = {
    color: 'var(--icon-default)',
    data: data,
    label: 'Market open/close',
  };

  const range =
    Math.max(...data.map(item => item.value)) -
    Math.min(...data.map(item => item.value));
  const yAxisFormatType = range > 4 ? 'percent' : 'percent1f';

  const getVerticalLines = () => {
    const verticalLines = [
      {
        x: new Date(
          fromDate.getUTCFullYear(),
          fromDate.getUTCMonth(),
          fromDate.getUTCDate(),
          9,
          30,
          0,
          0
        ),
      },
      {
        x: new Date(
          fromDate.getUTCFullYear(),
          fromDate.getUTCMonth(),
          fromDate.getUTCDate(),
          16,
          0,
          0,
          0
        ),
      },
      {
        x: new Date(
          toDate.getUTCFullYear(),
          toDate.getUTCMonth(),
          toDate.getUTCDate(),
          9,
          30,
          0,
          0
        ),
      },
      {
        x: new Date(
          toDate.getUTCFullYear(),
          toDate.getUTCMonth(),
          toDate.getUTCDate(),
          16,
          0,
          0,
          0
        ),
      },
    ];

    return verticalLines.filter(line =>
      data.some(item => isSameMinute(item.date, line.x))
    );
  };

  return (
    <S.Container>
      <S.Legend>
        <S.LegendItemVector />
        <S.LegendItemLabel>
          {t('earnings:earningsInsight.drawer.earningsRecap.marketOpenClose')}
        </S.LegendItemLabel>
      </S.Legend>
      {line.data.length > 0 && (
        <PriceChangesChart
          lines={[line]}
          fillColors={fillColors}
          yAxisFormatType={yAxisFormatType}
          thresholdData={data}
          verticalLines={getVerticalLines()}
        />
      )}
    </S.Container>
  );
};
